import { ResourceClient } from "@dl/web-core-js/api";
import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Card, Segmented, cn, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Info } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { refusalMessage, SupplierClient, type PartGroupDTO } from "../../api/manufacturing";
import {
  PartReplenishmentClient,
  ReplenishmentClient,
  replenishmentKeys,
  type NegativeFix,
  type NegativeStock,
  type SafetyDaysReview,
  type SupplierLeadTimeReview,
} from "../../api/replenishment";
import { SupplierLink } from "../../components/SupplierLink";
import { NexpimView } from "../../views";
import { leadSourceLabel } from "./replenishment/MaterialStory";
import { formatQuantity } from "./replenishment/shared";

/**
 * The reviews that keep the plan honest, meant to be read once a month.
 *
 * <ul>
 *   <li><b>Safety days</b> — materials that ran out, lived in red, or never
 *       came near their order point over the review window, with the safety
 *       days that would have fitted better.</li>
 *   <li><b>Lead times</b> — each supplier's typed lead time against what its
 *       receipts measure, and how often it keeps its promised dates.</li>
 *   <li><b>Negatives</b> — stock the ledger has below zero, almost all of it
 *       inherited from SIGM, each with the likeliest fix.</li>
 * </ul>
 *
 * <p>Nothing changes by itself. Every suggestion is a button a person
 * presses, and each button writes the same setting the setting's own screen
 * would.</p>
 */
type Tab = "safety" | "lead-times" | "negatives";

const PART_GROUP_PATH = "/manufacturing/part-group";

export function PurchasingReviewsPage() {
  const { t } = useTranslation();
  const { hasView } = useAuth();
  const allowed = hasView(NexpimView.PURCHASING);
  // The tab rides in the hash, so going to a part and coming back lands on the same review.
  const [tab, setTabState] = useState<Tab>(() => {
    const hash = window.location.hash.replace("#", "");
    return hash === "lead-times" || hash === "negatives" ? hash : "safety";
  });
  const setTab = (next: Tab) => {
    setTabState(next);
    try {
      window.history.replaceState(null, "", `#${next}`);
    } catch {
      // The hash is a convenience; the page works without it.
    }
  };

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.purchasing-reviews", "Purchasing reviews")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.purchasing-reviews", "Purchasing reviews")}
        subtitle={t("review.subtitle", "Once a month: safety days that did not fit, suppliers slower than they say, and stock below zero.")}
        actions={
          <Segmented<Tab>
            value={tab}
            onChange={setTab}
            options={[
              { value: "safety", label: t("review.tab.safety", "Safety days") },
              { value: "lead-times", label: t("review.tab.lead-times", "Lead times") },
              { value: "negatives", label: t("review.tab.negatives", "Negatives") },
            ]}
          />
        }
      />
      {tab === "safety" && <SafetyDaysTab />}
      {tab === "lead-times" && <LeadTimesTab />}
      {tab === "negatives" && <NegativesTab />}
    </>
  );
}

// ── safety days ─────────────────────────────────────────────────────────────

type Material = SafetyDaysReview["materials"][number];

function reasonLabel(reason: Material["reason"], t: (key: string, fallback: string) => string): string {
  switch (reason) {
    case "RAN_OUT":
      return t("review.reason.ran-out", "Ran out");
    case "MOSTLY_RED":
      return t("review.reason.mostly-red", "Mostly in red");
    default:
      return t("review.reason.never-low", "Never low");
  }
}

function reasonBadge(reason: Material["reason"], t: (key: string, fallback: string) => string) {
  const variant = reason === "RAN_OUT" ? "error" : reason === "MOSTLY_RED" ? "warning" : "info";
  return (
    <Badge variant={variant} size="sm">
      {reasonLabel(reason, t)}
    </Badge>
  );
}

/** The figure an override or a group holds: the suggestion without the top-seller extra, which is added on top. */
function baseDays(material: Material): number {
  return Math.max(0, material.suggested_safety_days - material.strategic_extra_days);
}

function SafetyDaysTab() {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const client = useMemo(() => new ReplenishmentClient(http), [http]);
  const overrides = useMemo(() => new PartReplenishmentClient(http), [http]);
  const groupsClient = useMemo(() => new ResourceClient<PartGroupDTO>(http, PART_GROUP_PATH), [http]);

  const { data, isLoading, isError } = useQuery({
    queryKey: replenishmentKeys.reviewSafety,
    queryFn: () => client.safetyDaysReview(),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: replenishmentKeys.reviewSafety });
    await queryClient.invalidateQueries({ queryKey: ["manufacturing-part-group", "all"] });
  };

  const applyToPart = useMutation({
    mutationFn: async (material: Material) => {
      const existing = (await overrides.find({ page: 0, size: 1, filters: { part_id: [String(material.part_id)] } })).content[0];
      if (existing) {
        await overrides.update({ ...existing, safety_days: baseDays(material) });
      } else {
        await overrides.create({
          id: 0,
          part_id: material.part_id,
          replenishment_policy: null,
          usage_source: null,
          safety_days: baseDays(material),
          fixed_minimum: null,
          fixed_maximum: null,
          average_daily_usage_override: null,
          average_daily_usage_override_reason: null,
          average_daily_usage_override_end_date: null,
          // The reason in words: this note is read on the part's page, not by a program.
          note: t("review.override-note", "Set by the safety-days review ({{reason}}).", { reason: reasonLabel(material.reason, t).toLowerCase() }),
        });
      }
      await queryClient.invalidateQueries({ queryKey: replenishmentKeys.part(material.part_id) });
    },
    onSuccess: async (_, material) => {
      toast.success(t("review.applied-part", "{{part}}: {{days}} safety days from the next run", { part: material.part_number, days: baseDays(material) }));
      await refresh();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const applyToGroup = useMutation({
    mutationFn: async ({ groupId, days }: { groupId: number; days: number }) => {
      const group = await groupsClient.findById(groupId);
      if (!group) throw new Error(t("review.group-missing", "That part group no longer exists."));
      await groupsClient.update({ ...group, safety_days: days });
      return group;
    },
    onSuccess: async (group, { days }) => {
      toast.success(t("review.applied-group", "Group {{code}}: {{days}} safety days from the next run", { code: group.code, days }));
      await refresh();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const groups = useMemo(() => {
    const byGroup = new Map<string, { id: number | null; code: string; name: string; current: number; materials: Material[] }>();
    for (const material of data?.materials ?? []) {
      const key = String(material.part_group_id ?? "none");
      const entry = byGroup.get(key) ?? {
        id: material.part_group_id,
        code: material.part_group_code ?? "",
        name: material.part_group_name ?? t("review.no-group", "No group"),
        current: material.group_safety_days,
        materials: [],
      };
      entry.materials.push(material);
      byGroup.set(key, entry);
    }
    return [...byGroup.values()];
  }, [data, t]);

  if (isError) {
    return (
      <div className="p-4 lg:p-6">
        <LoadError />
      </div>
    );
  }
  if (isLoading || !data) {
    return <Spinner />;
  }

  const shortDate = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" });
  const period = `${shortDate.format(new Date(`${data.from_date}T00:00:00`))} – ${shortDate.format(new Date(`${data.to_date}T00:00:00`))}`;

  return (
    <div className="flex flex-col gap-4 p-4 lg:p-6">
      <Card className="flex gap-3 p-4">
        <Info className="mt-0.5 size-4 shrink-0 text-info" />
        <div className="text-[12px] leading-relaxed text-ink-muted">
          <p>
            <span className="font-medium text-ink">{period}</span> ·{" "}
            {t("review.runs", "{{count}} days of runs", { count: data.run_count })}
          </p>
          <p>
            {t(
              "review.safety.rule",
              "Ran out: half again as many days (at least 2 more). More than half the window in red: a quarter more (at least 1). Never at its order point and above its maximum on average: a quarter fewer. A material needs 14 days of runs before it is judged.",
            )}
          </p>
        </div>
      </Card>
      {groups.length === 0 ? (
        <Card className="px-4 py-10 text-center text-[13px] text-ink-muted">
          <p className="font-medium text-ink">{t("review.safety.empty", "Nothing to change")}</p>
          <p className="mt-1">
            {data.run_count < 14
              ? t("review.safety.empty.young", "The review needs at least 14 days of nightly runs in the window; there are {{count}} so far.", {
                  count: data.run_count,
                })
              : t("review.safety.empty.fine", "Every material's safety days fitted over the window.")}
          </p>
        </Card>
      ) : (
        groups.map((group) => {
          const values = [...new Set(group.materials.map(baseDays))];
          const agreed = values.length === 1;
          const typical = mode(group.materials.map(baseDays));
          return (
            <Card key={`${String(group.id)}-${group.code}`} className="overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
                <div>
                  <h2 className="text-[14px] font-semibold text-ink">
                    {group.code} <span className="font-normal text-ink-muted">· {group.name}</span>
                  </h2>
                  <p className="text-[12px] text-ink-muted">
                    {t("review.group-days", "Group: {{days}} safety days · {{count}} to review", {
                      days: group.current,
                      count: group.materials.length,
                    })}
                  </p>
                </div>
                {canWrite && group.id !== null && typical !== group.current && (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={applyToGroup.isPending && applyToGroup.variables.groupId === group.id}
                    onClick={async () => {
                      const ok =
                        agreed ||
                        (await confirm({
                          title: t("review.group-confirm.title", "Set group {{code}} to {{days}} days?", { code: group.code, days: typical }),
                          message: t(
                            "review.group-confirm.message",
                            "Its materials do not all agree (from {{low}} to {{high}} days). {{days}} is the most common; every part in the group without its own figure follows it.",
                            { low: Math.min(...values), high: Math.max(...values), days: typical },
                          ),
                          confirmLabel: t("review.apply", "Apply"),
                        }));
                      if (ok && group.id !== null) applyToGroup.mutate({ groupId: group.id, days: typical });
                    }}
                  >
                    {t("review.apply-group", "Set group to {{days}} days", { days: typical })}
                  </Button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-[12px]">
                  <thead>
                    <tr className="bg-surface-sunk text-[11px] uppercase tracking-wide text-ink-faint">
                      <th className="px-4 py-2 text-left font-medium">{t("review.part", "Part")}</th>
                      <th className="px-2 py-2 text-left font-medium">{t("review.reason", "Why")}</th>
                      <th className="px-2 py-2 text-right font-medium">{t("review.observed", "Days seen")}</th>
                      <th className="px-2 py-2 text-right font-medium">{t("review.out", "Out")}</th>
                      <th className="px-2 py-2 text-right font-medium">{t("review.red", "In red")}</th>
                      <th className="px-2 py-2 text-right font-medium">{t("review.at-minimum", "≤ order point")}</th>
                      <th className="px-2 py-2 text-center font-medium">{t("review.safety-days", "Safety days")}</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line-soft">
                    {group.materials.map((material) => (
                      <tr key={material.part_id}>
                        <td className="px-4 py-2">
                          <Link
                            to="/manufacturing/parts/$partId"
                            params={{ partId: String(material.part_id) }}
                            className="font-medium text-accent-strong hover:underline"
                          >
                            {material.part_number}
                          </Link>
                          <span className="ml-2 text-ink-muted">{material.part_name}</span>
                        </td>
                        <td className="px-2 py-2">{reasonBadge(material.reason, t)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{material.days_observed}</td>
                        <td className={cn("px-2 py-2 text-right tabular-nums", material.days_out > 0 && "font-semibold text-risk")}>{material.days_out}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{material.days_red}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{material.days_at_or_below_minimum}</td>
                        <td className="px-2 py-2 text-center tabular-nums">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="text-ink-muted">{material.safety_days}</span>
                            <ArrowRight className="size-3 text-ink-faint" />
                            <span className="font-semibold text-ink">{material.suggested_safety_days}</span>
                          </span>
                          {material.strategic_extra_days > 0 && (
                            <span className="block text-[10px] text-ink-faint">
                              {t("review.includes-extra", "incl. +{{extra}} top sellers", { extra: material.strategic_extra_days })}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {/* Once the part holds the figure, the review's suggestion is done: no button to press again. */}
                          {canWrite && material.part_safety_days !== baseDays(material) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              loading={applyToPart.isPending && applyToPart.variables.part_id === material.part_id}
                              onClick={() => applyToPart.mutate(material)}
                            >
                              {material.part_safety_days !== null
                                ? t("review.apply-part.update", "Set part to {{days}}", { days: baseDays(material) })
                                : t("review.apply-part", "Give part {{days}}", { days: baseDays(material) })}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
}

function mode(values: number[]): number {
  const counts = new Map<number, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  let best = values[0] ?? 0;
  let bestCount = 0;
  for (const [value, count] of counts) {
    if (count > bestCount || (count === bestCount && value > best)) {
      best = value;
      bestCount = count;
    }
  }
  return best;
}

// ── lead times ──────────────────────────────────────────────────────────────

function LeadTimesTab() {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new ReplenishmentClient(http), [http]);
  const suppliers = useMemo(() => new SupplierClient(http), [http]);

  const { data, isLoading, isError } = useQuery({
    queryKey: replenishmentKeys.reviewLeadTimes,
    queryFn: () => client.leadTimeReview(),
  });

  const change = useMutation({
    mutationFn: async ({ row, action }: { row: SupplierLeadTimeReview; action: "measured" | "type" }) => {
      const supplier = await suppliers.findById(row.supplier_id);
      if (!supplier) throw new Error(t("review.supplier-missing", "That supplier no longer exists."));
      if (action === "measured") {
        await suppliers.update({ ...supplier, lead_time_mode: "MEASURED" });
      } else {
        await suppliers.update({ ...supplier, lead_time_days: Math.round(row.measured_days ?? 0) });
      }
    },
    onSuccess: async (_, { row, action }) => {
      toast.success(
        action === "measured"
          ? t("review.lead.now-measured", "{{supplier}} is planned with its measured lead time from the next run", { supplier: row.supplier_name })
          : t("review.lead.typed", "{{supplier}}: typed lead time set to {{days}} days", {
              supplier: row.supplier_name,
              days: Math.round(row.measured_days ?? 0),
            }),
      );
      await queryClient.invalidateQueries({ queryKey: replenishmentKeys.reviewLeadTimes });
      await queryClient.invalidateQueries({ queryKey: ["supplier", "all"] });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const cols = useMemo(() => {
    const c = defineColumns<SupplierLeadTimeReview>();
    return [
      c.custom(
        "supplier_name",
        (row) => row.supplier_name,
        (row) => (
          <span>
            <SupplierLink id={row.supplier_id} className="font-medium">
              {row.supplier_name}
            </SupplierLink>
            <span className="ml-2 text-[11px] text-ink-faint">{row.supplier_code}</span>
            {row.is_drifting && (
              <Badge variant="warning" size="sm" className="ml-2">
                {t("review.lead.drifting", "drifting")}
              </Badge>
            )}
          </span>
        ),
        { header: t("review.supplier", "Supplier"), priority: 1 },
      ),
      c.text("mode", (row) => (row.mode === "MEASURED" ? t("review.lead.mode.measured", "Measured") : t("review.lead.mode.fixed", "Typed")), {
        header: t("review.lead.mode", "Mode"),
        filter: "select",
        filterOptions: [
          { value: t("review.lead.mode.measured", "Measured"), label: t("review.lead.mode.measured", "Measured") },
          { value: t("review.lead.mode.fixed", "Typed"), label: t("review.lead.mode.fixed", "Typed") },
        ],
        priority: 3,
      }),
      c.number("fixed_days", (row) => row.fixed_days, { header: t("review.lead.typed-days", "Typed (d)"), filter: "range", priority: 2 }),
      c.number("measured_days", (row) => row.measured_days, { header: t("review.lead.measured-days", "Measured (d)"), filter: "range", priority: 1 }),
      c.number("receipt_count", (row) => row.receipt_count, { header: t("review.lead.receipts", "Receipts"), filter: "range", priority: 3 }),
      c.custom(
        "on_time_rate",
        (row) => row.on_time_rate ?? -1,
        (row) =>
          row.on_time_rate === null ? (
            <span className="text-ink-faint"></span>
          ) : (
            <span className={cn("tabular-nums", row.on_time_rate < 0.8 ? "text-risk" : row.on_time_rate < 0.95 ? "text-warn" : "text-ok")}>
              {Math.round(row.on_time_rate * 100)}%
              <span className="ml-1 text-[11px] text-ink-faint">/ {row.promised_count}</span>
            </span>
          ),
        { header: t("review.lead.on-time", "On time"), filter: "none", priority: 2 },
      ),
      c.custom(
        "effective_days",
        (row) => row.effective_days,
        (row) => (
          <span>
            <span className="font-semibold tabular-nums">{row.effective_days}</span>
            <span className="ml-1.5 text-[11px] text-ink-faint">{leadSourceLabel(row.source, t)}</span>
          </span>
        ),
        { header: t("review.lead.planned", "Planned with"), filter: "none", priority: 1 },
      ),
      c.number("material_count", (row) => row.material_count, { header: t("review.lead.materials", "Materials"), filter: "range", priority: 3 }),
      c.actions((row) =>
        canWrite && row.measured_days !== null ? (
          <span className="inline-flex gap-1">
            {row.mode !== "MEASURED" && (
              <Button
                variant="ghost"
                size="sm"
                loading={change.isPending && change.variables.row.supplier_id === row.supplier_id && change.variables.action === "measured"}
                onClick={() => change.mutate({ row, action: "measured" })}
              >
                {t("review.lead.use-measured", "Use measured")}
              </Button>
            )}
            {Math.round(row.measured_days) !== row.fixed_days && (
              <Button
                variant="ghost"
                size="sm"
                loading={change.isPending && change.variables.row.supplier_id === row.supplier_id && change.variables.action === "type"}
                onClick={() => change.mutate({ row, action: "type" })}
              >
                {t("review.lead.type-measured", "Type {{days}} d", { days: Math.round(row.measured_days) })}
              </Button>
            )}
          </span>
        ) : null,
      ),
    ];
  }, [t, canWrite, change]);

  return (
    <div className="flex flex-col gap-4 p-4 lg:p-6">
      <Card className="flex gap-3 p-4">
        <Info className="mt-0.5 size-4 shrink-0 text-info" />
        <p className="text-[12px] leading-relaxed text-ink-muted">
          {t(
            "review.lead.rule",
            "Measured = the median days from an order's release to the first receipt of each line, over the settings' window. Only orders released from nexpim count: SIGM's dates cannot tell issue from release. A supplier in Measured mode is planned with it once it has enough receipts; drifting means measured and typed disagree by more than the settings allow.",
          )}
        </p>
      </Card>
      {isError && <LoadError />}
      <DataTable<SupplierLeadTimeReview>
        data={data ?? []}
        columns={cols}
        getRowId={(row) => String(row.supplier_id)}
        loading={isLoading}
        stateKey="/purchasing/reviews/lead-times"
        emptyMessage={t("review.lead.empty", "No supplier planned or received from yet.")}
      />
    </div>
  );
}

// ── negatives ───────────────────────────────────────────────────────────────

const FIXES: NegativeFix[] = ["OUTPUT", "POLICY", "COUNT", "TRANSFER"];

function NegativesTab() {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new ReplenishmentClient(http), [http]);
  const [fix, setFix] = useState<NegativeFix | "ALL">("ALL");

  const { data, isLoading, isError } = useQuery({
    queryKey: replenishmentKeys.negatives,
    queryFn: () => client.negatives(),
  });

  const counts = useMemo(() => {
    const result: Record<string, number> = {};
    for (const row of data ?? []) result[row.fix] = (result[row.fix] ?? 0) + 1;
    return result;
  }, [data]);
  const rows = useMemo(() => (fix === "ALL" ? (data ?? []) : (data ?? []).filter((row) => row.fix === fix)), [data, fix]);

  const fixLabel = (value: NegativeFix) =>
    ({
      OUTPUT: t("review.fix.output", "Output never posted"),
      POLICY: t("review.fix.policy", "Never bought"),
      COUNT: t("review.fix.count", "Count it"),
      TRANSFER: t("review.fix.transfer", "Booked elsewhere"),
    })[value];
  const fixExplanation = (value: NegativeFix) =>
    ({
      OUTPUT: t("review.fix.output.detail", "Made in house: its components were issued but its output never posted, which is how SIGM ran. Post the output, or count it."),
      POLICY: t("review.fix.policy.detail", "Bought, yet never received or ordered: probably made in house or an expense. Fix its type or inventory policy."),
      COUNT: t("review.fix.count.detail", "Bought and received before, nowhere else to find it: count it and adjust."),
      TRANSFER: t("review.fix.transfer.detail", "Another warehouse holds it: used here but booked there. Transfer it."),
    })[value];
  const fixVariant = { OUTPUT: "info", POLICY: "warning", COUNT: "neutral", TRANSFER: "success" } as const;

  const warehouses = useMemo(() => [...new Set((data ?? []).map((row) => row.warehouse_code))].sort(), [data]);

  const cols = useMemo(() => {
    const c = defineColumns<NegativeStock>();
    return [
      c.custom(
        "part_number",
        (row) => row.part_number,
        (row) => (
          <span>
            <Link
              to="/manufacturing/parts/$partId"
              params={{ partId: String(row.part_id) }}
              className="font-medium text-accent-strong hover:underline"
            >
              {row.part_number}
            </Link>
            <span className="ml-2 text-[11px] text-ink-muted">{row.part_name}</span>
          </span>
        ),
        { header: t("review.part", "Part"), filter: "text", priority: 1 },
      ),
      c.text("warehouse_code", (row) => row.warehouse_code, {
        header: t("review.warehouse", "Warehouse"),
        filter: "select",
        filterOptions: warehouses.map((code) => ({ value: code, label: code })),
        priority: 2,
      }),
      c.custom(
        "quantity",
        (row) => row.quantity,
        (row) => (
          <span className="font-semibold tabular-nums text-risk">
            {formatQuantity(row.quantity)}
            <span className="ml-1 text-[11px] font-normal text-ink-faint">{row.stock_unit_code}</span>
          </span>
        ),
        { header: t("review.quantity", "Quantity"), filter: "range", align: "right", priority: 1 },
      ),
      c.custom(
        "elsewhere",
        (row) => row.elsewhere.map((other) => other.warehouse_code),
        (row) =>
          row.elsewhere.length === 0 ? (
            <span className="text-ink-faint"></span>
          ) : (
            <span className="text-[12px] tabular-nums text-ink-muted">
              {row.elsewhere.map((other) => `${other.warehouse_code} ${formatQuantity(other.quantity)}`).join(" · ")}
            </span>
          ),
        { header: t("review.elsewhere", "Elsewhere"), filter: "none", priority: 3 },
      ),
      c.text("last_movement_date", (row) => row.last_movement_date ?? "", {
        header: t("review.last-movement", "Last movement"),
        filter: "daterange",
        priority: 3,
      }),
      c.custom(
        "fix",
        (row) => row.fix,
        (row) => (
          <span className="inline-flex items-center gap-2" title={fixExplanation(row.fix)}>
            <Badge variant={fixVariant[row.fix]} size="sm">
              {fixLabel(row.fix)}
            </Badge>
            {row.fix === "TRANSFER" && (
              <Link to="/manufacturing/stock/transfer" className="text-[12px] text-accent-strong hover:underline">
                {t("review.fix.transfer.go", "Transfer")}
              </Link>
            )}
          </span>
        ),
        { header: t("review.fix", "Likeliest fix"), filter: "none", priority: 1 },
      ),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, warehouses]);

  return (
    <div className="flex flex-col gap-4 p-4 lg:p-6">
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {FIXES.map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={fix === value}
            onClick={() => setFix(fix === value ? "ALL" : value)}
            className={cn(
              "flex flex-col gap-1 rounded-lg border bg-surface p-3 text-left shadow-card transition",
              fix === value ? "border-accent ring-1 ring-accent" : "border-line hover:border-ink-faint",
            )}
          >
            <span className="flex items-center justify-between gap-2">
              <Badge variant={fixVariant[value]} size="sm">
                {fixLabel(value)}
              </Badge>
              <span className="text-[18px] font-semibold tabular-nums text-ink">{counts[value] ?? 0}</span>
            </span>
            <span className="text-[11px] leading-snug text-ink-muted">{fixExplanation(value)}</span>
          </button>
        ))}
      </div>
      {isError && <LoadError />}
      <DataTable<NegativeStock>
        data={rows}
        columns={cols}
        getRowId={(row) => `${String(row.part_id)}-${String(row.warehouse_id)}`}
        loading={isLoading}
        stateKey="/purchasing/reviews/negatives"
        emptyMessage={t("review.negatives.empty", "Nothing below zero. The ledger agrees with itself.")}
      />
    </div>
  );
}

function LoadError() {
  const { t } = useTranslation();
  return (
    <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">{t("review.error", "This review could not be loaded.")}</p>
  );
}

function Spinner() {
  return (
    <div className="flex h-40 items-center justify-center">
      <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
    </div>
  );
}
